import { useCallback, useState } from "react";
import { useMutation } from "react-query";

import type {
  AgeVerificationScriptConfigItem,
  Config,
  InitAsyncVerificationPayload,
  InitAsyncVerificationResponse,
  VerifyResponse,
  VerifyVariables,
} from "@jti/fe-common";
import { getConfig } from "@jti/fe-common";

import { MUTATION_KEYS } from "../../../constants/queryKeys";
import { AvpAPI } from "../../../services";

declare global {
  interface Window {
    transactionlink: {
      setContext: (token?: string) => void;
      open: () => void;
      on: (event: "widget-opened" | "error" | "widget-closed" | "workflow-finished", callback: () => void) => void;
    };
  }
}

type AsyncAgeVerificationScriptConfig = {
  token?: string;
  onSuccess: () => void;
};

type UseAvpActions = {
  verify: (variables: VerifyVariables) => Promise<VerifyResponse>;
  getAsyncMethodConfig: (methodName?: string) => AgeVerificationScriptConfigItem | undefined;
  initAsyncVerification: (variables: InitAsyncVerificationPayload) => Promise<InitAsyncVerificationResponse>;
  loadAsyncAgeVerificationScript: (
    methodCode: string,
    response: InitAsyncVerificationResponse,
    config: AsyncAgeVerificationScriptConfig
  ) => void;
  asyncVerificationPending: boolean;
  isAsyncVerificationFailed: boolean;
  asyncVerificationData?: InitAsyncVerificationResponse;
  isVerifing: boolean;
};

export const TRANSACTIONLINK_KEY = "transactionlink";

export const useAvpActions = (): UseAvpActions => {
  const componentsList: Config["AGE_VERIFICATION_ASYNC_SCRIPTS_CONFIG"] =
    getConfig().AGE_VERIFICATION_ASYNC_SCRIPTS_CONFIG;

  const avp = AvpAPI.init();

  const [loadedScripts, setLoadedScripts] = useState<string[]>([]);
  const callVerify = useMutation(MUTATION_KEYS.avpVerify, (variables: VerifyVariables) => avp.verify(variables));
  const verify = useCallback(
    async (variables: VerifyVariables) => {
      const response = await callVerify.mutateAsync(variables);
      return response;
    },
    [callVerify]
  );

  const callInitAsyncVerification = useMutation(
    MUTATION_KEYS.avpInitAsyncVerification,
    (data: InitAsyncVerificationPayload) => avp.initAsyncVerification(data)
  );
  const initAsyncVerification = useCallback(
    async (variables: InitAsyncVerificationPayload) => {
      return callInitAsyncVerification.mutateAsync(variables);
    },
    [callInitAsyncVerification]
  );

  const transactionlinkReady = ({ onSuccess, token }: AsyncAgeVerificationScriptConfig) => {
    const { transactionlink } = window;
    if (!transactionlink) {
      setTimeout(transactionlinkReady, 10, { onSuccess, token });
      return;
    }
    transactionlink.setContext(token);
    transactionlink.open();
    transactionlink.on("workflow-finished", onSuccess);
  };

  const onLoadExternalScript = (component: string, { onSuccess, token }: AsyncAgeVerificationScriptConfig): void => {
    if (component === TRANSACTIONLINK_KEY) {
      transactionlinkReady({ token, onSuccess });
    }
  };

  const getAsyncMethodConfig = (methodName?: string) => {
    if (!methodName) {
      return;
    }
    return componentsList?.find(({ code }) => code === methodName);
  };

  const loadAsyncAgeVerificationScript = (
    methodCode: string,
    { data }: InitAsyncVerificationResponse,
    config: AsyncAgeVerificationScriptConfig
  ) => {
    if (data?.component) {
      const { component } = data;
      const script = getAsyncMethodConfig(methodCode)?.scriptUrl;
      if (script) {
        onLoadScript(script, component, config);
      }
    }
  };

  const onLoadScript = async (script: string, component: string, config: AsyncAgeVerificationScriptConfig) => {
    if (script) {
      const isAlreadyLoaded = loadedScripts.includes(component);
      if (!isAlreadyLoaded) {
        await addScriptToPage(script);
        onLoadExternalScript(component, config);
        setLoadedScripts((prevState) => [...prevState, component]);
      } else {
        onLoadExternalScript(component, config);
      }
    }
  };

  const addScriptToPage = (scriptUrl: string) => {
    return new Promise<void>((resolve) => {
      const script = document.createElement("script");
      script.onload = () => {
        resolve();
      };
      script.src = scriptUrl;
      script.async = true;
      document.body.appendChild(script);
    });
  };

  return {
    verify,
    initAsyncVerification,
    loadAsyncAgeVerificationScript,
    getAsyncMethodConfig,
    asyncVerificationPending: callInitAsyncVerification.isLoading,
    asyncVerificationData: callInitAsyncVerification.data,
    isAsyncVerificationFailed: callInitAsyncVerification.isError,
    isVerifing: callVerify.isLoading,
  };
};
